import { useState, useEffect } from 'react';
import { ImageIcon, X, Plus, Trash2, Search, User, UtensilsCrossed } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { useTables } from '../../hooks/useTables';
import { useProducts } from '../../hooks/useProducts';
import { useMenuTypes } from '../../hooks/useMenuTypes';
// import { useSettings } from '../../hooks/useSettings';
import { useVAT } from '../../hooks/useVAT';
// import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';

interface OrderItem {
  productId: string;
  productName: string;
  productSku?: string;
  price: number;
  quantity: number;
  discount: number;
  total: number;
  notes?: string;
  category?: string;
  image?: string;
}

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateOrder: (orderData: any) => void;
}

export function CreateOrderModal({ isOpen, onClose, onCreateOrder }: CreateOrderModalProps) {
  const { user } = useAuth();
  // Order Type Selection
  const [orderType, setOrderType] = useState<'table' | 'customer'>('customer');
  
  const [currentStep, setCurrentStep] = useState<'type' | 'customer' | 'items' | 'review'>('type');
  
  // Customer Information
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'MPESA' | 'CARD'>('CASH');
  const [orderNotes, setOrderNotes] = useState('');
  
  // Order Items
  const [selectedItems, setSelectedItems] = useState<OrderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState('All');

  // Fetch data from backend
  const { customers, loading: loadingCustomers } = useCustomers();
  const { tables, loading: loadingTables } = useTables();
  const { products: dbProducts} = useProducts({ page: 1, limit: 500 });
  const { menuTypes } = useMenuTypes(user?.tenantId || user?.restaurantId);
  const { calculateOrderVAT, getVATInfo } = useVAT();

  const resolveProductImage = (product: { id: string; name: string; images?: string[] | string | null }) => {
    const fallback = `https://via.placeholder.com/400x300?text=${encodeURIComponent(product.name || 'No Image')}`;

    const firstImage = Array.isArray(product.images)
      ? product.images.find((img) => typeof img === 'string' && img.trim().length > 0)
      : typeof product.images === 'string' && product.images.trim().length > 0
      ? product.images
      : '';

    if (!firstImage) {
      return null;
    }

    if (firstImage.startsWith('http://') || firstImage.startsWith('https://') || firstImage.startsWith('data:')) {
      return firstImage;
    }

    try {
      const apiOrigin = new URL(API_BASE_URL).origin;
      const normalizedPath = firstImage.startsWith('/') ? firstImage : `/${firstImage}`;
      return `${apiOrigin}${normalizedPath}`;
    } catch {
      return firstImage;
    }
  };
  
  // Transform database products to match the expected format
  const products = dbProducts.map(product => ({
    id: product.id,
    name: product.name,
    price: Number(product.price),
    category: product.category?.name || 'Uncategorized',
    menuTypeId: product.category?.menuTypeId || null,
    sku: product.sku,
    image: resolveProductImage({
      id: product.id,
      name: product.name,
      images: product.images,
    }),
  }));

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('type');
      setOrderType('customer');
      setSelectedCustomerId('');
      setSelectedTableId('');
      setSelectedItems([]);
      setSearchQuery('');
      setSelectedMenuTypeId('All');
      setPaymentMethod('CASH');
      setOrderNotes('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const availableMenuTypeIds = new Set(
    products
      .map((item) => item.menuTypeId)
      .filter((value): value is string => Boolean(value)),
  );

  const menuTypeOptions = [
    { id: 'All', name: 'All' },
    ...menuTypes
      .filter((menuType) => availableMenuTypeIds.has(menuType.id))
      .sort((a, b) => a.name.localeCompare(b.name)),
  ];

  // Filtered products for search + quick menu type filtering
  const filteredProducts = products.filter((item) => {
    const matchesMenuType = selectedMenuTypeId === 'All' || item.menuTypeId === selectedMenuTypeId;
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesMenuType && matchesSearch;
  });

  const addProduct = (product: typeof products[0]) => {
    const existingItem = selectedItems.find(item => item.productId === product.id);
    
    if (existingItem) {
      setSelectedItems(selectedItems.map(item =>
        item.productId === product.id
          ? { 
              ...item, 
              quantity: item.quantity + 1,
              total: item.price * (item.quantity + 1) * (1 - item.discount / 100)
            }
          : item
      ));
    } else {
      const newItem: OrderItem = {
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        price: product.price,
        quantity: 1,
        discount: 0,
        total: product.price,
        category: product.category,
        image: product.image ?? undefined,
      };
      setSelectedItems([...selectedItems, newItem]);
    }
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(productId);
    } else {
      setSelectedItems(selectedItems.map(item =>
        item.productId === productId 
          ? { 
              ...item, 
              quantity: newQuantity,
              total: item.price * newQuantity * (1 - item.discount / 100)
            }
          : item
      ));
    }
  };

  const removeItem = (productId: string) => {
    setSelectedItems(selectedItems.filter(item => item.productId !== productId));
  };

  // Calculate totals using VAT hook
  const cartItems = selectedItems.map(item => ({
    price: item.price,
    quantity: item.quantity,
  }));
  
  // const vatCalculation = calculateOrderVAT(cartItems);
  const itemsSubtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalDiscount = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity * item.discount / 100), 0);
  
  // Apply discount to VAT calculation
  const adjustedCartItems = cartItems.map(item => ({
    ...item,
    price: item.price * (1 - totalDiscount / itemsSubtotal || 0), // Distribute discount proportionally
  }));
  
  const finalVatCalc = calculateOrderVAT(adjustedCartItems);
  const subtotal = finalVatCalc.subtotal;
  const tax = finalVatCalc.vatAmount;
  const deliveryFee = orderType === 'customer' ? 5.00 : 0; // No delivery fee for table orders
  const total = finalVatCalc.total + deliveryFee;

  const handleNext = () => {
    if (currentStep === 'type') {
      setCurrentStep('customer');
    } else if (currentStep === 'customer') {
      if (orderType === 'customer' && !selectedCustomerId) {
        toast.error('Please select a customer');
        return;
      }
      if (orderType === 'table' && !selectedTableId) {
        toast.error('Please select a table');
        return;
      }
      setCurrentStep('items');
    } else if (currentStep === 'items') {
      if (selectedItems.length === 0) {
        toast.error('Please add at least one item to the order');
        return;
      }
      setCurrentStep('review');
    }
  };

  const handleBack = () => {
    if (currentStep === 'customer') {
      setCurrentStep('type');
    } else if (currentStep === 'items') {
      setCurrentStep('customer');
    } else if (currentStep === 'review') {
      setCurrentStep('items');
    }
  };

  // Sort tables numerically by extracting numbers from table names
  const sortedTables = [...tables].sort((a, b) => {
    const numA = parseInt(a.name.match(/\d+/)?.[0] || '0');
    const numB = parseInt(b.name.match(/\d+/)?.[0] || '0');
    return numA - numB;
  }).filter(t => t.isActive);

  // Handle double-click on order type to auto-advance
  const handleTypeDoubleClick = (type: 'table' | 'customer') => {
    setOrderType(type);
    handleNext();
  };

  // Handle double-click on customer to auto-advance
  const handleCustomerDoubleClick = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setCurrentStep('items');
  };

  // Handle double-click on table to auto-advance
  const handleTableDoubleClick = (tableId: string) => {
    setSelectedTableId(tableId);
    setCurrentStep('items');
  };

  const handleCreateOrder = () => {
    // Format items for backend
    const formattedItems = selectedItems.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      price: item.price,
      discount: item.discount,
      total: item.total,
      notes: item.notes || undefined,
    }));

    // Build notes for table orders
    let notes = orderNotes;
    if (orderType === 'table') {
      const selectedTable = tables.find(t => t.id === selectedTableId);
      if (selectedTable) {
        notes = `Table: ${selectedTable.name}${orderNotes ? '\n' + orderNotes : ''}`;
      }
    }

    // Format order data according to CreateOrderDto
    const newOrder = {
      customerId: orderType === 'customer' ? selectedCustomerId : undefined,
      tableId: orderType === 'table' ? selectedTableId : undefined,
      items: formattedItems,
      status: 'PENDING',
      subtotal: subtotal - totalDiscount,
      tax: tax,
      total: total,
      paymentMethod: paymentMethod,
      discount: totalDiscount,
      deliveryFee: deliveryFee > 0 ? deliveryFee : undefined,
      deliveryAddress: orderType === 'customer' ? customers.find(c => c.id === selectedCustomerId)?.address || undefined : undefined,
      notes: notes || undefined,
    };

    onCreateOrder(newOrder);
  };

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
  const selectedTable = tables.find(t => t.id === selectedTableId);

  return (
    <div className="fixed inset-0 bg-gray-900/20 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-950 rounded-2xl shadow-2xl shadow-black/30 border border-gray-200 dark:border-slate-800 w-full max-w-4xl relative max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Order</h2>
            <div className="flex items-center gap-2 mt-2">
              <div className={`flex items-center gap-2 text-sm ${currentStep === 'type' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'type' ? 'bg-indigo-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>1</div>
                Type
              </div>
              <div className="w-8 h-px bg-gray-300 dark:bg-gray-600" />
              <div className={`flex items-center gap-2 text-sm ${currentStep === 'customer' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'customer' ? 'bg-indigo-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>2</div>
                {orderType === 'table' ? 'Table' : 'Customer'}
              </div>
              <div className="w-8 h-px bg-gray-300 dark:bg-gray-600" />
              <div className={`flex items-center gap-2 text-sm ${currentStep === 'items' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'items' ? 'bg-indigo-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>3</div>
                Items
              </div>
              <div className="w-8 h-px bg-gray-300 dark:bg-gray-600" />
              <div className={`flex items-center gap-2 text-sm ${currentStep === 'review' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep === 'review' ? 'bg-indigo-600 text-white' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>4</div>
                Review
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Order Type Selection */}
          {currentStep === 'type' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Order Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setOrderType('customer')}
                  onDoubleClick={() => handleTypeDoubleClick('customer')}
                  className={`p-6 border-2 rounded-xl transition-all cursor-pointer ${
                    orderType === 'customer'
                      ? 'border-indigo-600 bg-slate-950 text-white dark:border-indigo-500 dark:bg-indigo-950/70'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 hover:border-indigo-400'
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${
                      orderType === 'customer' ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-slate-800'
                    }`}>
                      <User className={`w-8 h-8 ${orderType === 'customer' ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Customer Order</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Delivery or takeout order for a customer</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Double-click to continue</p>
                  </div>
                </button>

                <button
                  onClick={() => setOrderType('table')}
                  onDoubleClick={() => handleTypeDoubleClick('table')}
                  className={`p-6 border-2 rounded-xl transition-all cursor-pointer ${
                    orderType === 'table'
                      ? 'border-indigo-600 bg-slate-950 text-white dark:border-indigo-500 dark:bg-indigo-950/70'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-950 hover:border-indigo-400'
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${
                      orderType === 'table' ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-slate-800'
                    }`}>
                      <UtensilsCrossed className={`w-8 h-8 ${orderType === 'table' ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Table Order</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Dine-in order for a table</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Double-click to continue</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Customer/Table Selection */}
          {currentStep === 'customer' && (
            <div className="space-y-4">
              {orderType === 'customer' ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Customer</h3>
                  {loadingCustomers ? (
                    <p className="text-center text-gray-500 dark:text-gray-400">Loading customers...</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {customers.map((customer) => (
                        <button
                          key={customer.id}
                          onClick={() => setSelectedCustomerId(customer.id)}
                          onDoubleClick={() => handleCustomerDoubleClick(customer.id)}
                          className={`w-full p-4 border-2 rounded-lg text-left transition-all cursor-pointer hover:shadow-md ${
                            selectedCustomerId === customer.id
                              ? 'border-indigo-600 bg-slate-950 text-white dark:border-indigo-500 dark:bg-indigo-950/70'
                              : 'border-gray-200 dark:border-slate-700 hover:border-indigo-400'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {customer.firstName} {customer.lastName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{customer.email}</p>
                              {customer.phone && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">{customer.phone}</p>
                              )}
                            </div>
                            {selectedCustomerId === customer.id && (
                              <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Table</h3>
                  {loadingTables ? (
                    <p className="text-center text-gray-500 dark:text-gray-400">Loading tables...</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                      {sortedTables.map((table) => (
                        <button
                          key={table.id}
                          onClick={() => setSelectedTableId(table.id)}
                          onDoubleClick={() => handleTableDoubleClick(table.id)}
                          className={`p-4 border-2 rounded-lg transition-all cursor-pointer hover:shadow-md ${
                            selectedTableId === table.id
                              ? 'border-indigo-600 bg-slate-950 text-white dark:border-indigo-500 dark:bg-indigo-950/70'
                              : 'border-gray-200 dark:border-slate-700 hover:border-indigo-400'
                          }`}
                        >
                          <div className="text-center">
                            <p className="font-bold text-lg text-gray-900 dark:text-white">{table.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Capacity: {table.capacity}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Double-click to select</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Payment Method */}
              <div className="mt-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="CASH">Cash</option>
                  <option value="MPESA">M-Pesa</option>
                  <option value="CARD">Credit/Debit Card</option>
                </select>
              </div>

              {/* Order Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Order Notes (Optional)
                </label>
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Any special instructions..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Step 3: Select Items */}
          {currentStep === 'items' && (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search menu items..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Selected Items */}
              {selectedItems.length > 0 && (
                <div className="bg-gray-50 dark:bg-slate-950 rounded-lg p-4 border border-gray-200 dark:border-slate-800">
                  <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-3">Selected Items ({selectedItems.length})</h3>
                  <div className="space-y-2">
                    {selectedItems.map((item) => (
                      <div key={item.productId} className="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">{item.productName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">KSh {item.price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-semibold text-gray-900 dark:text-white">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                              +
                            </button>
                          </div>
                          <span className="w-20 text-right font-semibold text-gray-900 dark:text-white">
                            KSh {item.total.toFixed(2)}
                          </span>
                          <button
                            onClick={() => removeItem(item.productId)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <span className="font-semibold text-gray-900 dark:text-white">Total:</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-white">KSh {total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Available Menu Items */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Available Menu Items</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {menuTypeOptions.map((menuType) => (
                    <button
                      key={menuType.id}
                      type="button"
                      onClick={() => setSelectedMenuTypeId(menuType.id)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        selectedMenuTypeId === menuType.id
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-indigo-400'
                      }`}
                    >
                      {menuType.name}
                    </button>
                  ))}
                </div>
                {filteredProducts.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    No products found for this filter.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                  {filteredProducts.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => addProduct(item)}
                      className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-md transition-all text-left"
                    >                   
                      {item.image && item.image.trim() ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer">
                          <ImageIcon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{item.category}</p>
                        <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">KSh {item.price.toFixed(2)}</p>
                      </div>
                      <Plus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review Order */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              {/* Customer/Table Information */}
              <div className="bg-gray-50 dark:bg-slate-950 rounded-lg p-4 border border-gray-200 dark:border-slate-800">
                <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-3">
                  {orderType === 'table' ? 'Table Information' : 'Customer Information'}
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {orderType === 'customer' && selectedCustomer ? (
                    <>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Name:</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Phone:</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedCustomer.phone || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500 dark:text-gray-400">Email:</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedCustomer.email}</p>
                      </div>
                      {selectedCustomer.address && (
                        <div className="col-span-2">
                          <p className="text-gray-500 dark:text-gray-400">Address:</p>
                          <p className="font-medium text-gray-900 dark:text-white">{selectedCustomer.address}</p>
                        </div>
                      )}
                    </>
                  ) : orderType === 'table' && selectedTable ? (
                    <>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Table:</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedTable.name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Capacity:</p>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedTable.capacity} seats</p>
                      </div>
                    </>
                  ) : null}
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Payment:</p>
                    <p className="font-medium text-gray-900 dark:text-white">{paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Type:</p>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">{orderType}</p>
                  </div>
                  {orderNotes && (
                    <div className="col-span-2">
                      <p className="text-gray-500 dark:text-gray-400">Notes:</p>
                      <p className="font-medium text-gray-900 dark:text-white">{orderNotes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-gray-50 dark:bg-slate-950 rounded-lg p-4 border border-gray-200 dark:border-slate-800">
                <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-3">Order Items</h3>
                <div className="space-y-2">
                  {selectedItems.map((item) => (
                    <div key={item.productId} className="flex justify-between items-center text-sm">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{item.productName}</p>
                        <p className="text-gray-500 dark:text-gray-400">Qty: {item.quantity} × KSh {item.price.toFixed(2)}</p>
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white">KSh {item.total.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  {getVATInfo().mode === 'Inclusive' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Amount (Excl. VAT)</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        KSh {subtotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      VAT {getVATInfo().rate} {getVATInfo().mode === 'Inclusive' ? '(Included)' : ''}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">KSh {tax.toFixed(2)}</span>
                  </div>
                  {getVATInfo().mode === 'Inclusive' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Items Total</span>
                      <span className="font-medium text-gray-900 dark:text-white">KSh {(subtotal + tax).toFixed(2)}</span>
                    </div>
                  )}
                  {getVATInfo().mode !== 'Inclusive' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                      <span className="font-medium text-gray-900 dark:text-white">KSh {subtotal.toFixed(2)}</span>
                    </div>
                  )}
                  {deliveryFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Delivery Fee:</span>
                      <span className="font-medium text-gray-900 dark:text-white">KSh {deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400 pt-1">
                    <span>ℹ️</span>
                    <span>{getVATInfo().description}</span>
                  </div>
                  <div className="pt-2 border-t border-indigo-200 dark:border-indigo-700 flex justify-between">
                    <span className="font-bold text-gray-900 dark:text-white">Total to Pay:</span>
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">KSh {total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-slate-950 border-t border-gray-200 dark:border-slate-800 p-6 flex items-center justify-between">
          <button
            onClick={currentStep === 'type' ? onClose : handleBack}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium"
          >
            {currentStep === 'type' ? 'Cancel' : 'Back'}
          </button>
          <button
            onClick={currentStep === 'review' ? handleCreateOrder : handleNext}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            {currentStep === 'review' ? 'Create Order' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}